import React from 'react';
import PropTypes from "prop-types";
import './style.scss';
import {FormattedMessage} from 'react-intl';

import defaultGoCarLogo from './go-car.svg';

const bookingStatusIdMap = {
  "created": "BookingStatus.created",
  "driver-found": "BookingStatus.driverFound",
  "picked-up": "BookingStatus.pickedUp",
  "completed": "BookingStatus.completed",
};

const goSendBookingStatusIdMap = {
  "created": "GoSend.BookingStatus.created",
  "driver-found": "GoSend.BookingStatus.driverFound",
  "picked-up": "GoSend.BookingStatus.pickedUp",
  "completed": "GoSend.BookingStatus.completed",
  "rejected": "GoSend.BookingStatus.rejected"
};

const goSendPartnerBookingStatusIdMap = {
  "created": "GoSendPartner.BookingStatus.created",
  "driver-found": "GoSend.BookingStatus.driverFound",
  "picked-up": "GoSend.BookingStatus.pickedUp",
  "completed": "GoSend.BookingStatus.completed",
  "rejected": "GoSendPartner.BookingStatus.rejected"
};

const isGoSendBooking = (serviceType) => {
  return (serviceType === "go-kilat-new" || serviceType === "go-send-instant-car" || serviceType === "go-send-instant-2w");
}

const isGoSendHematBooking = (serviceType, customerType) => {
  return (serviceType === "instant-mpmd" && customerType === "consumer")
}

const isStatusPickedUp = status => (status === "picked-up")

const isStatusOnHold = status => (status === "on-hold")

const isGoSendReturnBooking = (status, subStatus) => (isStatusPickedUp(status) && isStatusOnHold(subStatus))

export default class BookingDetails extends React.Component {
  static propTypes = {
    customerName: PropTypes.string.isRequired,
    serviceType: PropTypes.string.isRequired,
    countryCode: PropTypes.string,
    status: PropTypes.string.isRequired,
    pickupEta: PropTypes.number,
    dropoffEta: PropTypes.number,
    isGoSendPartnerOrder: PropTypes.bool,
    isGoSendPartnerOrderOnHold: PropTypes.bool,
    subStatus: PropTypes.string,
    isReblast: PropTypes.bool,
    serviceTypeLogo: PropTypes.string,
    customerType: PropTypes.string,
    isCODBooking: PropTypes.bool
  };

  divRef = React.createRef();

  computedNumericStyleValue = (div, property) => {
    return parseFloat(window.getComputedStyle(div).getPropertyValue(property));
  }

  cardHeightWithPadding = () => {
    const div = this.divRef.current;
    if (div) {
      return parseFloat(div.clientHeight) + this.computedNumericStyleValue(div, "padding-top");
    }
    return 0;
  }

  serviceTypeLogo() {
    return ( this.props.serviceTypeLogo || defaultGoCarLogo );
  }

  getBookingStatusMessageId() {
    if (this.props.isGoSendPartnerOrder) {
      return this.getGoSendPartnerBookingMessageId();
    } else if (isGoSendBooking(this.props.serviceType)) {
      return this.getGoSendBookingStatusMessageId();
    } else if (isGoSendHematBooking(this.props.serviceType, this.props.customerType)) {
      return this.getGoSendHematBookingStatusMessageId();
    } else {
      return this.getDefaultBookingStatusMessageId();
    }
  }

  getGoSendPartnerBookingMessageId() {
    const { status, pickupEta, dropoffEta, isGoSendPartnerOrderOnHold, subStatus, isReblast } = this.props;

    if(status === "created" && isReblast)
      return "GoSendPartner.BookingStatus.created.reblast"

    if (isGoSendReturnBooking(status, subStatus))
      if(dropoffEta)
        return "GoSend.BookingStatus.returnWithETA"
      else
        return "GoSend.BookingStatus.return"

    if (isGoSendPartnerOrderOnHold)
      if (subStatus === "allocated")
        return "GoSendPartner.BookingStatus.allocated.workingOnOtherDelivery"
      else if (isStatusOnHold(subStatus))
        return "GoSendPartner.BookingStatus.OnHold"
      else
        return "GoSendPartner.BookingStatus.picked.workingOnOtherDelivery"
    if (status === "driver-found" && pickupEta)
      return "GoSend.BookingStatus.driverFoundWithETA";
    if (isStatusPickedUp(status) && dropoffEta)
      return "GoSend.BookingStatus.pickedUpWithETA";
    return goSendPartnerBookingStatusIdMap[status] || "EmptyString";
  }

  getGoSendBookingStatusMessageId() {
    if (this.props.status === "driver-found" && this.props.pickupEta)
      return "GoSend.BookingStatus.driverFoundWithETA";

    if (isGoSendReturnBooking(this.props.status, this.props.subStatus))
      if(this.props.dropoffEta)
        return "GoSend.BookingStatus.returnWithETA"
      else
        return "GoSend.BookingStatus.return"

    if (isStatusPickedUp(this.props.status) && this.props.dropoffEta)
      return "GoSend.BookingStatus.pickedUpWithETA"
    return goSendBookingStatusIdMap[this.props.status] || "EmptyString";
  }

  getGoSendHematBookingStatusMessageId() {
    let { subStatus } = this.props;
    if (!subStatus){
      subStatus = this.props.status
    }

    switch (subStatus) {
      case "created":
        return "GoSendHemat.BookingSubStatus.created";
      case "allocated":
        return "GoSendHemat.BookingSubStatus.allocated";
      case "outForPickup":
        return "GoSendHemat.BookingSubStatus.outForPickup";
      case "picked":
        return "GoSendHemat.BookingSubStatus.picked";
      case "driver-found":
        return "GoSendHemat.BookingSubStatus.outForPickup";
      case "outForDelivery":
        return "GoSendHemat.BookingSubStatus.outForDelivery";
      case "delivered":
        return "GoSendHemat.BookingSubStatus.delivered";
      case "completed":
        return "GoSendHemat.BookingSubStatus.delivered";
      case "onHold":
        return "GoSendHemat.BookingSubStatus.onHold";
      default:
        return "GoSendHemat.BookingSubStatus.outForDelivery";
    }
  }

  getDefaultBookingStatusMessageId() {
    if (this.props.status === "driver-found" && this.props.pickupEta)
      return "BookingStatus.driverFoundWithETA";
    if (isStatusPickedUp(this.props.status) && this.props.dropoffEta)
      return "BookingStatus.pickedUpWithETA";
    return bookingStatusIdMap[this.props.status] || "EmptyString";
  }

  getCODBookingMessage() {
    const { status, subStatus, pickupEta, dropoffEta} = this.props;
    const isCODBooking = this.props.isCODBooking
    
    if (!isCODBooking) return null;
    
    const isPickup = status === "driver-found";
    const isDropOff = status === "picked-up";
    
    if (isGoSendReturnBooking(status, subStatus)) {
      return dropoffEta
      ? [
        "GoSend.COD.headerTitleReturn",
        { id: "GoSend.COD.headerTitleReturnWithETA", values: { eta: dropoffEta } },
      ]
      : ["GoSend.COD.headerTitleReturn"];
    }

    if (isPickup) {
      return pickupEta
      ? [
         "GoSend.COD.headerTitlePickUp",
         { id: "GoSend.COD.headerTitlePickUpWithETA", values: { eta: pickupEta } },
        ]
        : ["GoSend.COD.headerTitlePickUp"];
    }
      
    if (isDropOff) {
      return dropoffEta
      ? [
        "GoSend.COD.headerTitleDropOff",
        { id: "GoSend.COD.headerTitleDropOffWithETA", values: { eta: dropoffEta } },
        ]
        : ["GoSend.COD.headerTitleDropOff"];
    }
    
    return null;
  }

  getCustomerName() {
    if (this.props.isGoSendPartnerOrder || isGoSendBooking(this.props.serviceType)) {
      return;
    }
    return (
      <div className="customerName">
        {this.props.customerName}
      </div>
    );
  }

  getEta() {
    if (this.props.isGoSendPartnerOrderOnHold)
      return null;

    if (this.props.status === "driver-found" && this.props.pickupEta)
      return (
        <div>
          {this.props.pickupEta}
          {' '}
          <FormattedMessage
            id={"etaUnit"}
          />
        </div>
      );
    else if (isStatusPickedUp(this.props.status) && this.props.dropoffEta)
      return (
        <div>
          {this.props.dropoffEta}
          {' '}
          <FormattedMessage
            id={"etaUnit"}
          />
        </div>
      );
    return null;
  }

  render() {
    const serviceTypeLogo = this.serviceTypeLogo();
    const bookingStatusMessageId = this.getBookingStatusMessageId();
    const eta = this.getEta();
    const customerName = this.getCustomerName();
    const codMessages = this.getCODBookingMessage();

    if (codMessages) {
      return (
      <div className="BookingDetails" ref={this.divRef}>
        <div className="leftColumn">
          {typeof codMessages[0] === "string" && (
            <div className="codMessage">
              <FormattedMessage id={codMessages[0]} />
            </div>
          )}
          <div className="codEta">
            {codMessages.length > 1 && typeof codMessages[1] === "object" && (
            <>
            <FormattedMessage id={codMessages[1].id} />
            {' '}
            <span className="eta">
              {codMessages[1].values.eta}{' '}
              <FormattedMessage id="etaUnit" />
            </span>
            </>
            )}
          </div>
        </div>
        <div className="rightColumn">
          <img alt="Product Name" src={serviceTypeLogo} />
        </div>
      </div> 
      );
    } else {
      return (
        <div className="BookingDetails" ref={this.divRef}>
          <div className="leftColumn">
            {customerName}
            {' '}
            <FormattedMessage
              id={bookingStatusMessageId}
            />
            {' '}
            <div className="eta">
              {eta}
            </div>
          </div>
          <div className="rightColumn">
            <img alt="Product Name" src={serviceTypeLogo}/>
          </div>
        </div>
      );
    }
  }
}
